import React from 'react';
import { toast } from 'sonner';
import { PanelTable } from '../../components/display/PanelTable/PanelTable';
import { SectionCard } from '../../components/sections/SectionCard';
import { generateBillingSchedulePhasesData } from '@shared/data/mockBillingSchedules';
import type { PanelTableColumn, ExpandableConfig } from '../../components/display/PanelTable/types';
import type { BillingSchedule, BillingSchedulePhaseData, BillingScheduleProduct } from '@features/billing-schedules/types/billing-schedule.types';

interface PhasesSectionProps {
  isExpanded?: boolean;
  onToggle?: () => void;
  isDetailView?: boolean;
  billingSchedule?: BillingSchedule;
  className?: string;
}

const phaseColumns: PanelTableColumn<BillingSchedulePhaseData>[] = [
  {
    key: 'name',
    label: 'NAME',
    headerClassName: 'w-64',
    cellClassName: 'font-medium text-sm text-foreground',
  },
  {
    key: 'period',
    label: 'PERIOD',
    cellClassName: 'text-sm text-foreground',
  }
];

const itemColumns: PanelTableColumn<BillingScheduleProduct>[] = [
  {
    key: 'name',
    label: 'ITEM',
    headerClassName: 'w-[300px]',
    render: (_value, item) => {
      return (
        <div className="flex flex-col gap-0.5">
          <div className="font-medium text-sm text-foreground leading-none">
            {item.name}
          </div>
          <div className="text-xs font-normal text-muted-foreground">
            {item.type}
          </div>
        </div>
      );
    },
  },
  {
    key: 'repeats',
    label: 'REPEATS',
    headerClassName: 'w-[150px]',
    cellClassName: 'text-sm text-foreground',
  },
  {
    key: 'billed',
    label: 'BILLED',
    headerClassName: 'w-[150px]',
    cellClassName: 'text-sm text-foreground',
  },
  {
    key: 'discount',
    label: 'DISCOUNT',
    headerClassName: 'w-[120px]',
    cellClassName: 'text-sm text-foreground',
  },
  {
    key: 'amount',
    label: 'AMOUNT',
    headerClassName: 'w-[140px]',
    cellClassName: 'font-semibold text-sm text-foreground',
  }
];

const phaseRowActions = (phase: BillingSchedulePhaseData) => [
  {
    key: 'update-phase',
    label: 'Update phase',
    onClick: () => { toast.info(`Update phase "${phase.name}" — coming soon`) }
  },
  {
    key: 'cancel-phase',
    label: 'Cancel phase',
    onClick: () => { toast.info(`Cancel phase "${phase.name}" — coming soon`) }
  },
  {
    key: 'remove-phase',
    label: 'Remove phase',
    onClick: () => { toast.info(`Remove phase "${phase.name}" — coming soon`) },
    variant: 'destructive' as const
  }
];

const itemRowActions = (item: BillingScheduleProduct) => [
  {
    key: 'view-item',
    label: 'View item',
    onClick: () => { toast.info(`View item "${item.name}" — coming soon`) }
  },
  {
    key: 'update-item',
    label: 'Update item',
    onClick: () => { toast.info(`Update item "${item.name}" — coming soon`) }
  },
  {
    key: 'remove-item',
    label: 'Remove item',
    onClick: () => { toast.info(`Remove item "${item.name}" — coming soon`) },
    variant: 'destructive' as const
  }
];

const expandableConfig: ExpandableConfig<BillingSchedulePhaseData, BillingScheduleProduct> = {
  expandedColumns: itemColumns,
  getExpandedData: (phase) => phase.items,
  showExpandIcon: true,
  expandedRowActions: itemRowActions,
};

export const PhasesSection = React.memo<PhasesSectionProps>(({
  isExpanded = true,
  onToggle,
  isDetailView = false,
  billingSchedule,
  className,
}) => {
  const phasesData = generateBillingSchedulePhasesData(billingSchedule);

  return (
    <SectionCard
      title="Phases"
      isCollapsible={!isDetailView}
      isCollapsed={!isExpanded}
      onTitleClick={onToggle}
      className={className}
    >
      <PanelTable<BillingSchedulePhaseData, BillingScheduleProduct>
        data={phasesData}
        columns={phaseColumns}
        variant="expandable"
        expandableConfig={expandableConfig}
        showPagination={false}
        rowActions={phaseRowActions}
      />
    </SectionCard>
  );
});

PhasesSection.displayName = 'PhasesSection';
